/*
 * IW Nuevo header behaviors. Loaded with defer from sections/header.liquid.
 *
 * 1. Mega menu close button: closes its own menu and returns focus to the summary.
 * 2. Mega menu overlay: keeps the dim overlay's top edge on the open panel's bottom edge.
 * 3. Inline search: gives the results listbox its own id.
 * 4. Inline search: shows the short mobile placeholder below 990px.
 */
(() => {
  // 1. Delegated, so it keeps working when the theme editor re-renders the header section.
  document.addEventListener('click', (event) => {
    const button = event.target.closest('.iw-mega__close');
    if (!button) return;

    const menu = button.closest('header-menu');
    if (!menu) return;

    // HeaderMenu (assets/details-disclosure.js) extends DetailsDisclosure, which has close().
    if (typeof menu.close === 'function') {
      menu.close();
    } else {
      menu.querySelector('details')?.removeAttribute('open');
    }
    menu.querySelector('summary')?.focus();
  });

  // 2. The overlay is a fixed pseudo-element of the panel (assets/iw-header.css) whose top is --iw-mega-bottom. The panel's
  // bottom edge moves with the sticky header (stuck, hidden or scrolling away), so it is measured when a menu opens and
  // again on scroll, resize and the header's own top transition.
  let openPanel = null;
  let frame = 0;

  const placeOverlay = () => {
    frame = 0;
    if (openPanel) openPanel.style.setProperty('--iw-mega-bottom', `${openPanel.getBoundingClientRect().bottom}px`);
  };

  const scheduleOverlay = () => {
    if (openPanel && !frame) frame = requestAnimationFrame(placeOverlay);
  };

  // The toggle event does not bubble, so it is caught in the capture phase.
  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.classList.contains('mega-menu')) return;

      const panel = details.querySelector('.iw-mega');
      if (!panel) return;

      if (details.open) {
        openPanel = panel;
        placeOverlay();
      } else if (openPanel === panel) {
        openPanel = null;
      }
    },
    true
  );
  window.addEventListener('scroll', scheduleOverlay, { passive: true });
  window.addEventListener('resize', scheduleOverlay);
  document.addEventListener('transitionend', (event) => {
    if (event.target.classList?.contains('section-header')) scheduleOverlay();
  });

  // 3. assets/predictive-search.js injects sections/predictive-search.liquid, whose root is
  // <div id="predictive-search-results" role="listbox">. The modal search (snippets/header-search.liquid) uses the same
  // markup, so the id would repeat if both had results. The inline field points aria-owns and aria-controls at
  // #predictive-search-results-inline, and this observer renames the listbox injected inside the inline field.
  const INLINE_RESULTS_ID = 'predictive-search-results-inline';
  const observed = new WeakSet();

  const renameInlineResults = (container) => {
    const listbox = container.querySelector('#predictive-search-results');
    if (listbox) listbox.id = INLINE_RESULTS_ID;
  };

  const watchInlineSearch = () => {
    document.querySelectorAll('.iw-search [data-predictive-search]').forEach((container) => {
      if (observed.has(container)) return;
      observed.add(container);
      new MutationObserver(() => renameInlineResults(container)).observe(container, { childList: true });
      renameInlineResults(container);
    });
  };

  // 4. The field has one placeholder attribute. snippets/iw-header-search-inline.liquid renders the desktop text there and the
  // mobile text in data-placeholder-mobile; below 990px (the breakpoint of assets/iw-header.css) the mobile text is shown.
  const mobileQuery = window.matchMedia('(max-width: 989px)');

  const applyPlaceholder = () => {
    document.querySelectorAll('.iw-search__input[data-placeholder-mobile]').forEach((input) => {
      if (input.dataset.placeholderDesktop === undefined) {
        input.dataset.placeholderDesktop = input.getAttribute('placeholder') || '';
      }
      input.setAttribute(
        'placeholder',
        mobileQuery.matches ? input.dataset.placeholderMobile : input.dataset.placeholderDesktop
      );
    });
  };

  const onSectionLoad = () => {
    watchInlineSearch();
    applyPlaceholder();
  };

  // Bind the listbox-id fix and the editor reload first, so an old browser
  // failing below never skips them.
  watchInlineSearch();
  document.addEventListener('shopify:section:load', onSectionLoad);

  applyPlaceholder();
  // Safari < 14 only has the deprecated addListener on MediaQueryList.
  if (typeof mobileQuery.addEventListener === 'function') {
    mobileQuery.addEventListener('change', applyPlaceholder);
  } else if (typeof mobileQuery.addListener === 'function') {
    mobileQuery.addListener(applyPlaceholder);
  }
})();
